import { publicEstimateSearch } from '@/lib/sales/public-estimate-search';
import { proposalCheckoutEstimateId } from '@/lib/sales/proposal-navigation';
import { ProposalCheckout } from '@/components/public/ProposalCheckout';
import type { Proposal } from '@/types/proposal';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PortalStatusPill, type PortalPillTone } from '@/components/portal/PortalStatusPill';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { Checkbox } from '@/components/atoms/checkbox';
import { Input } from '@/components/atoms/input';
import SignaturePad, { type SignaturePadHandle } from '@/components/molecules/SignaturePad';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { formatDateOnly } from '@/lib/dates/date-only';
import {
  proposalOptionDefaultOfferKeys,
  proposalOptionSelectionChanges,
} from '@/lib/sales/proposalOptionBreakdown';
import {
  mergeOptionAttachments,
  proposalApproveRecap,
  proposalMembershipOffer,
  proposalOptionDepositDue,
  proposalOptionDepositBeforeApproval,
  proposalOptionDisplayTotal,
} from '@/lib/sales/proposalOptionMembership';
import {
  ProposalOptionDocument,
  type ProposalOptionDocumentCustomer,
} from '@/components/public/ProposalOptionDocument';
import {
  ProposalApproveModal,
  type ProposalApproveSignature,
} from '@/components/public/ProposalApproveModal';
import { SingleScopeActionBar } from '@/components/public/SingleScopeActionBar';
import { ProposalItemizedOptionDocument } from '@/components/public/ProposalItemizedOptionDocument';
import { ProposalOptionTabs } from '@/components/public/ProposalOptionTabs';
import { ProposalPricingStatus } from '@/components/public/ProposalPricingStatus';
import { useProposalSelectionPreview } from '@/hooks/proposals/useProposalSelectionPreview';
import { resolveCustomerTerms, type ResolvedCustomerTerms } from '@/lib/terms/clientTerms';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import {
  usePortalDeclineProposal,
  usePortalProposal,
  usePortalSelectOption,
  type PortalProposalDetail,
} from '@/hooks/portal/usePortalProposals';
import { PortalShell } from '@/components/portal/PortalShell';
import { DocumentActions, DocumentBanner, Letterhead } from '@/components/portal/PortalDocument';
import { PORTAL_ACCENT_BTN, daysUntil } from '@/lib/portal/documentHelpers';
import { ApiError } from '@/lib/api/errors';
import { registerSignedProposalPdf } from '@/lib/proposals/registerSignedProposalPdf';
import { isSingleScopeLayout, singleScopeThemeVars } from '@/lib/quote/singleScopeTheme';
import { layoutVisibility } from '@/lib/quote/layoutVisibility';
import type { ProposalOption, ProposalOptionAttachment } from '@/types/proposal';

const ACTIONABLE = new Set(['SENT', 'VIEWED', 'FOLLOW_UP']);
const RESPOND_ANCHOR = 'proposal-respond';

export const Route = createFileRoute('/(portal)/portal/proposals/$id')({
  validateSearch: publicEstimateSearch,
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalProposalDetailPage,
});

const STATUS_PILL: Record<string, { label: string; tone: PortalPillTone }> = {
  SENT: { label: 'Awaiting you', tone: 'accent' },
  VIEWED: { label: 'Awaiting you', tone: 'accent' },
  FOLLOW_UP: { label: 'Awaiting you', tone: 'accent' },
  RESOLVED: { label: 'Accepted', tone: 'success' },
  DECLINED: { label: 'Declined', tone: 'neutral' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
};

function statusOf(status: string): { label: string; tone: PortalPillTone } {
  return STATUS_PILL[status] ?? { label: status.toLowerCase(), tone: 'neutral' };
}

function scrollToRespond() {
  document.getElementById(RESPOND_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function PortalProposalDetailPage() {
  const { id } = Route.useParams();
  const proposal = usePortalProposal(id);
  const notFound = proposal.error instanceof ApiError && proposal.error.status === 404;
  const crumb = proposal.data ? `Proposal ${proposal.data.proposalNumber}` : undefined;

  return (
    <PortalShell
      back={{
        label: 'Estimates',
        to: '/portal/estimates',
        crumb,
        // Both open the tenant's proposal PDF (public PDF-viewer route) with the
        // browser's native Save + Print.
        actions: proposal.data ? <DocumentActions pdfHref={`/pdf-proposal/${id}`} /> : undefined,
      }}
    >
      {proposal.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : notFound ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            This proposal isn&apos;t available.
          </CardContent>
        </Card>
      ) : proposal.isError ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            We couldn&apos;t load this proposal. Please try again.
          </CardContent>
        </Card>
      ) : (
        <ProposalDetail data={proposal.data} id={id} />
      )}
    </PortalShell>
  );
}

function ProposalDetail({ data, id }: { data: PortalProposalDetail; id: string }) {
  const status =
    data.status === 'RESOLVED' && !data.customerAcceptedAt
      ? { label: 'Option selected', tone: 'neutral' as const }
      : statusOf(data.status);
  const options = data.options ?? [];
  const actionable = ACTIONABLE.has(data.status);
  const company = data.company ?? null;
  // Each option uses the same resolved design as its individual estimate.
  const singleScope = isSingleScopeLayout(data.resolvedDocLayout);
  const ssBrand = data.resolvedDocLayout?.branding ?? null;

  const recommendedIndex = Math.max(
    0,
    options.findIndex((o) => o.recommended),
  );
  const resolvedIndex =
    typeof data.selectedOptionIndex === 'number' ? data.selectedOptionIndex : null;

  const select = usePortalSelectOption(id);
  // Open payment after choosing a required-deposit option, or after accepting
  // an option with an optional deposit and recording its signed copy.
  const [payDepositOnLoad, setPayDepositOnLoad] = useState(false);
  const decline = usePortalDeclineProposal(id);
  // The tab being compared is independent of the persisted approved option.
  // Checkout always receives data.selectedOptionIndex, including in read-only views.
  const [chosen, setChosen] = useState(resolvedIndex ?? recommendedIndex);
  useEffect(() => {
    if (resolvedIndex != null) setChosen(resolvedIndex);
  }, [resolvedIndex]);
  const [terms, setTerms] = useState(false);
  // A proposal sent for a signature is approved with the pad, not a click.
  const signatureRequired = data.signatureRequired === true;
  const [sigName, setSigName] = useState('');
  const sigPadRef = useRef<SignaturePadHandle>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  // Per-option join-&-save membership state (public-page parity), keyed by index.
  const [membershipSelections, setMembershipSelections] = useState<Record<number, boolean>>({});
  const [membershipBillingSelections, setMembershipBillingSelections] = useState<
    Record<number, string>
  >({});
  const billingIdFor = (option: ProposalOption, idx: number) =>
    membershipBillingSelections[idx] ?? proposalMembershipOffer(option)?.billingOptionId ?? null;
  // Legacy itemized options remain static. New payloads support server-priced
  // choices; absent local picks use the option's stored selection.
  const [optionalSelections, setOptionalSelections] = useState<Record<number, Set<string>>>({});
  const selectedKeysFor = (option: ProposalOption, idx: number) =>
    optionalSelections[idx] ?? proposalOptionDefaultOfferKeys(option);
  const toggleOptional = (idx: number, option: ProposalOption, offerKey: string) =>
    setOptionalSelections((current) => {
      const next = new Set(current[idx] ?? proposalOptionDefaultOfferKeys(option));
      if (next.has(offerKey)) next.delete(offerKey);
      else next.add(offerKey);
      return { ...current, [idx]: next };
    });
  const pricingPreview = useProposalSelectionPreview({
    proposal: { ...data, updatedAt: data.updatedAt ?? '' },
    enabled: actionable,
    optionalSelections,
    membershipSelections,
    membershipBillingSelections,
  });
  const { optionFor } = pricingPreview;
  const chosenPricing = pricingPreview.stateFor(chosen);

  // Single-scope approve flow — the estimate's floating pill + modal.
  const [approveOpen, setApproveOpen] = useState(false);
  useEffect(() => {
    if (!chosenPricing.ready) setApproveOpen(false);
  }, [chosenPricing.ready]);
  const proposalTerms = resolveCustomerTerms(
    typeof data.terms === 'string' ? data.terms : undefined,
  );
  const issued = data.createdAt
    ? formatDateTimeInTimeZone(data.createdAt, data.timezone, { dateStyle: 'medium' })
    : null;
  const validUntilFmt = data.validUntil ? formatDateOnly(data.validUntil) : null;
  const docCustomer: ProposalOptionDocumentCustomer | null = data.customer ?? null;
  // The document and approval controls share the chosen option's live total.
  const chosenPriced = options[chosen] ? optionFor(chosen) : undefined;

  // Single-scope follows the individual estimate: the deliberate Approve/sign
  // action IS the acceptance, so there is no separate "I accept the terms" tick.
  useEffect(() => {
    if (singleScope) setTerms(true);
  }, [singleScope]);

  const minTotal = options.length ? Math.min(...options.map((o) => o.total)) : 0;
  const validDays = data.validUntil ? daysUntil(data.validUntil) : NaN;
  const showExpiry = actionable && data.validUntil && !Number.isNaN(validDays) && validDays >= 0;

  const requiredDeposit = chosenPriced
    ? proposalOptionDepositBeforeApproval(
        chosenPriced,
        membershipSelections[chosen] === true,
        billingIdFor(options[chosen], chosen),
      )
    : 0;
  const depositFirst = requiredDeposit > 0;
  const hasPaymentPlan = options[chosen]?.paymentScheduleConfigured === true;
  const checkoutFirst = depositFirst || hasPaymentPlan;
  // Optional deposits open after acceptance; required deposits open before it.
  const paysDeposit = Boolean(
    chosenPriced &&
    proposalOptionDepositDue(
      chosenPriced,
      membershipSelections[chosen] === true,
      billingIdFor(options[chosen], chosen),
    ) > 0,
  );
  const onApprove = async (signatureArg?: ProposalApproveSignature | null) => {
    if (!actionable || select.isPending || !chosenPriced || !chosenPricing.ready) return;
    // Single-scope approves through the modal, which validated terms + signature
    // and passes the collected signature (or null). The classic sticky bar calls
    // with no argument, so it validates + collects here.
    let signature: { name: string; image: string } | null = signatureArg ?? null;
    if (!checkoutFirst && signatureArg === undefined) {
      if (!terms) {
        toast.error('Please accept the terms before approving.');
        scrollToRespond();
        return;
      }
      if (signatureRequired) {
        const image = sigPadRef.current?.getDataURL() ?? null;
        const name = sigName.trim();
        if (!name || !image) {
          toast.error('Please type your name and draw your signature before approving.');
          scrollToRespond();
          return;
        }
        signature = { name, image };
      }
    }
    setApproveOpen(false);
    try {
      const chosenOption = options[chosen];
      const billingId = chosenOption ? billingIdFor(chosenOption, chosen) : null;
      const offer = chosenOption ? proposalMembershipOffer(chosenOption, billingId) : null;
      const memberSel = membershipSelections[chosen] === true;
      // Send only changed rows. Even an unchanged selection payload makes the
      // server reprice a frozen proposal before materializing its estimate.
      const keys = chosenOption ? selectedKeysFor(chosenOption, chosen) : new Set<string>();
      const lineItems = chosenOption ? proposalOptionSelectionChanges(chosenOption, keys) : [];
      const updated = await select.mutateAsync({
        optionIndex: chosen,
        ...(depositFirst
          ? { depositFirst: true }
          : checkoutFirst
            ? { checkoutFirst: true }
            : { signature }),
        ...(data.selectionPreviewSupported === true
          ? { expectedTotal: proposalOptionDisplayTotal(chosenPriced, memberSel, billingId) }
          : {}),
        ...(lineItems.length ? { lineItems } : {}),
        ...(offer
          ? {
              acceptMembershipOffer: memberSel,
              ...(memberSel && billingId ? { membershipBillingOptionId: billingId } : {}),
            }
          : {}),
      });
      if (!checkoutFirst && signature) {
        // The signed document of record. Best-effort: the acceptance has
        // already landed and is durable without it.
        await registerSignedProposalPdf({
          opsBaseUrl: import.meta.env.VITE_OPERATIONS_URL ?? '',
          id,
          reason: 'signature_collected',
          content: {
            proposal: { ...updated, signatureDataUrl: signature.image },
            company: {
              name: updated.company?.name ?? 'Your provider',
              // The branding the customer just saw on screen. Dropping it here
              // made the signed copy differ by which surface accepted it.
              logoUrl: updated.company?.logoUrl ?? null,
              // Single-scope signed PDFs render the inline logo (a remote logoUrl
              // CORS-fails), so carry it or the tenant logo drops to initials.
              logoDataUri: updated.company?.logoDataUri ?? null,
            },
          },
        }).catch(() => undefined);
      }
      toast.success(
        depositFirst
          ? 'Option selected. Pay the deposit to continue.'
          : checkoutFirst
            ? 'Option selected. Review the payment plan to continue to approval.'
            : 'Option approved. Thank you!',
      );
      // Only now: a Checkout redirect must not cut the signed copy short.
      setPayDepositOnLoad(depositFirst || (!checkoutFirst && paysDeposit));
    } catch (error) {
      toast.error(
        error instanceof ApiError &&
          (error.data as { code?: string } | undefined)?.code === 'SCHEDULE_REQUIRED'
          ? 'A payment plan is required before approval. Please contact the office to confirm it.'
          : error instanceof Error && error.message
            ? error.message
            : 'We couldn’t approve this option. Please try again.',
      );
    }
  };

  const onDecline = async () => {
    try {
      await decline.mutateAsync(reason.trim() || undefined);
      toast.success('Proposal declined.');
      setDeclining(false);
    } catch {
      toast.error('We couldn’t decline this proposal. Please try again.');
    }
  };

  return (
    <div
      className="space-y-4 pb-4"
      style={singleScope ? (singleScopeThemeVars(ssBrand) as CSSProperties) : undefined}
    >
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {showExpiry ? (
          <DocumentBanner icon={<Clock className="size-4" />}>
            <span className="font-medium">Valid until {formatDateOnly(data.validUntil!)}</span>
            {validDays === 0
              ? ' — expires today.'
              : ` — ${validDays} ${validDays === 1 ? 'day' : 'days'} left.`}{' '}
            Prices hold until then.
          </DocumentBanner>
        ) : null}

        <div className="space-y-6 p-5 md:p-6">
          <Letterhead
            name={company?.name?.trim() || 'Your provider'}
            eyebrow="Proposal"
            eyebrowValue={data.proposalNumber}
          />

          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight text-balance">
                {actionable ? 'Choose your option' : 'Your proposal'}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {actionable ? (
                  <>
                    {options.length} {options.length === 1 ? 'option' : 'options'}
                    {minTotal > 0 ? ` from ${formatCurrency(minTotal)}` : ''} · pick the one that
                    fits.
                  </>
                ) : (
                  'Your selected scope and approval details are below.'
                )}
              </p>
            </div>
            {!actionable ? <PortalStatusPill label={status.label} tone={status.tone} /> : null}
          </div>

          {actionable && options[chosen] && !chosenPricing.ready ? (
            <ProposalPricingStatus error={chosenPricing.error} onRetry={pricingPreview.retry} />
          ) : null}

          {options.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              This proposal has no options.
            </p>
          ) : (
            <ProposalPortalTabs
              singleScope={singleScope}
              proposal={data}
              options={options.map((o, i) => optionFor(i) ?? o)}
              activeIndex={chosen}
              actionable={actionable}
              onSelect={setChosen}
              canToggleOptionals={
                actionable && (singleScope || data.selectionPreviewSupported === true)
              }
              onToggleOptional={(idx, offerKey) => {
                const raw = options[idx];
                if (raw) toggleOptional(idx, raw, offerKey);
              }}
              membershipSelections={membershipSelections}
              onMembershipSelectedChange={(idx, sel) =>
                setMembershipSelections((c) => ({ ...c, [idx]: sel }))
              }
              billingIdFor={billingIdFor}
              onMembershipBillingChange={(idx, id) =>
                setMembershipBillingSelections((c) => ({ ...c, [idx]: id }))
              }
              customer={docCustomer}
              notes={data.notes}
              issued={issued}
              validUntil={validUntilFmt}
              attachments={data.attachments}
              terms={proposalTerms}
            />
          )}
        </div>
      </div>

      {proposalCheckoutEstimateId(data) ? (
        <ProposalCheckout proposal={data as Proposal} portal openDepositOnLoad={payDepositOnLoad} />
      ) : null}

      {/* Accept / decline. The sticky bar carries the primary approve; terms live
          here so the customer sees them before approving. */}
      {actionable ? (
        <div id={RESPOND_ANCHOR}>
          <Card>
            <CardContent className="space-y-3 py-4">
              {/* Single-scope carries the terms + signature in the approve modal;
                  only the decline path lives here. Classic keeps its inline tick +
                  signature. */}
              {!singleScope && !checkoutFirst ? (
                <>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={terms} onCheckedChange={(v) => setTerms(v === true)} />
                    <span>I accept the terms of this proposal.</span>
                  </label>
                  {signatureRequired ? (
                    <div className="space-y-3 border-t border-border pt-3">
                      <div className="space-y-1">
                        <Label htmlFor="proposal-signature-name">Your name</Label>
                        <Input
                          id="proposal-signature-name"
                          placeholder="Full name"
                          autoComplete="name"
                          value={sigName}
                          onChange={(e) => setSigName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>Draw your signature</Label>
                        <SignaturePad ref={sigPadRef} />
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
              {declining ? (
                <div className="space-y-3 border-t border-border pt-3">
                  <div className="space-y-1">
                    <Label htmlFor="proposal-decline-reason">Reason (optional)</Label>
                    <Textarea
                      id="proposal-decline-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Let the business know why (optional)"
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      variant="destructive"
                      disabled={decline.isPending}
                      onClick={() => void onDecline()}
                    >
                      {decline.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                      Confirm decline
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setDeclining(false)}
                      disabled={decline.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground"
                  onClick={() => setDeclining(true)}
                >
                  Decline this proposal
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Sticky action bar — pick above, approve here. Approve materializes the
          estimate server-side; no card is charged from this screen. Single-scope
          uses the estimate's floating pill → approve modal. */}
      {actionable && options[chosen] && !declining && !(singleScope && approveOpen) ? (
        singleScope ? (
          <SingleScopeActionBar
            eyebrow={
              chosen === recommendedIndex && options[chosen].recommended
                ? 'Recommended option'
                : options[chosen].label || `Option ${chosen + 1}`
            }
            total={proposalOptionDisplayTotal(
              chosenPriced ?? options[chosen],
              membershipSelections[chosen] === true,
              billingIdFor(options[chosen], chosen),
            )}
            fmt={formatCurrency}
            primaryLabel={
              depositFirst
                ? `Pay ${formatCurrency(requiredDeposit)} deposit to approve`
                : hasPaymentPlan
                  ? 'Review payment plan'
                  : paysDeposit
                    ? 'Approve and pay'
                    : signatureRequired
                      ? 'Sign & approve'
                      : 'Approve'
            }
            primaryDisabled={select.isPending || !chosenPricing.ready}
            onPrimary={() => {
              if (!chosenPricing.ready || select.isPending) return;
              if (checkoutFirst) void onApprove();
              else setApproveOpen(true);
            }}
          />
        ) : (
          <div className="sticky bottom-0 z-10 -mx-1 rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-xs uppercase tracking-wide text-muted-foreground">
                  {chosen === recommendedIndex && options[chosen].recommended
                    ? 'Recommended option'
                    : options[chosen].label}
                </p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatCurrency(
                    proposalOptionDisplayTotal(
                      chosenPriced ?? options[chosen],
                      membershipSelections[chosen] === true,
                      billingIdFor(options[chosen], chosen),
                    ),
                  )}
                </p>
              </div>
              <Button
                className={`shrink-0 ${PORTAL_ACCENT_BTN}`}
                disabled={select.isPending || !chosenPricing.ready}
                onClick={() => void onApprove()}
              >
                {select.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                {depositFirst
                  ? `Pay ${formatCurrency(requiredDeposit)} deposit to approve`
                  : hasPaymentPlan
                    ? 'Review payment plan'
                    : paysDeposit
                      ? 'Approve and pay'
                      : 'Approve & schedule'}
              </Button>
            </div>
          </div>
        )
      ) : null}

      {/* Single-scope approve modal — the estimate's approve sheet. */}
      {actionable && singleScope && options[chosen] && !checkoutFirst ? (
        <ProposalApproveModal
          open={approveOpen}
          onOpenChange={setApproveOpen}
          proposalNumber={data.proposalNumber}
          companyName={company?.name}
          customerName={data.customer?.name}
          signatureRequired={signatureRequired}
          terms={proposalTerms}
          recap={proposalApproveRecap(
            chosenPriced ?? options[chosen],
            membershipSelections[chosen] === true,
            billingIdFor(options[chosen], chosen),
            options[chosen].label || `Option ${chosen + 1}`,
          )}
          submitting={select.isPending || !chosenPricing.ready}
          themeVars={singleScopeThemeVars(ssBrand) as CSSProperties}
          onApprove={(sig) => void onApprove(sig)}
          submitLabel={paysDeposit ? 'Approve and pay' : undefined}
        />
      ) : null}
    </div>
  );
}

/** One designed estimate document per option, with the existing approval flow below. */
function ProposalPortalTabs({
  singleScope,
  proposal,
  options,
  activeIndex,
  actionable = false,
  onSelect,
  canToggleOptionals = false,
  onToggleOptional,
  membershipSelections,
  onMembershipSelectedChange,
  billingIdFor,
  onMembershipBillingChange,
  customer,
  notes,
  issued,
  validUntil,
  attachments,
  terms,
}: {
  singleScope: boolean;
  proposal: PortalProposalDetail;
  options: ProposalOption[];
  activeIndex: number;
  /** Only an actionable proposal offers the interactive join-&-save toggle; a
   *  resolved one shows the approved figures with no way to re-toggle. */
  actionable?: boolean;
  onSelect: (index: number) => void;
  /** Server-priced choices also enable itemized controls; legacy itemized stays static. */
  canToggleOptionals?: boolean;
  onToggleOptional?: (index: number, offerKey: string) => void;
  membershipSelections: Record<number, boolean>;
  onMembershipSelectedChange: (index: number, selected: boolean) => void;
  billingIdFor: (option: ProposalOption, index: number) => string | null;
  onMembershipBillingChange: (index: number, billingOptionId: string) => void;
  customer?: ProposalOptionDocumentCustomer | null;
  notes?: string | null;
  issued?: string | null;
  validUntil?: string | null;
  attachments?: ProposalOptionAttachment[];
  terms: ResolvedCustomerTerms;
}) {
  const active = options[activeIndex] ?? options[0];
  const activeBillingId = active ? billingIdFor(active, activeIndex) : null;
  // The offer + its live toggle only exist while the proposal can still be
  // accepted; a resolved proposal renders the approved total with no re-toggle.
  const activeMemberSel = actionable && membershipSelections[activeIndex] === true;
  const activeOffer =
    actionable && active ? proposalMembershipOffer(active, activeBillingId) : null;
  const documentProps = active
    ? {
        option: active,
        optionLabel: active.label || `Option ${activeIndex + 1}`,
        offer: activeOffer,
        membershipSelected: activeMemberSel,
        onMembershipSelectedChange: (selected: boolean) =>
          onMembershipSelectedChange(activeIndex, selected),
        billingOptionId: activeBillingId,
        onMembershipBillingChange: (billingId: string) =>
          onMembershipBillingChange(activeIndex, billingId),
        displayTotal: proposalOptionDisplayTotal(active, activeMemberSel, activeBillingId),
        depositDue: proposalOptionDepositDue(active, activeMemberSel, activeBillingId),
        canToggleOptionals,
        showFinancing: actionable,
        onToggleOptional: (offerKey: string) => onToggleOptional?.(activeIndex, offerKey),
        customer,
        notes,
        issued,
        validUntil,
        attachments: mergeOptionAttachments(attachments, active.attachments),
        terms,
      }
    : null;
  return (
    <ProposalOptionTabs
      options={options.map((option, index) => ({
        label: option.label || `Option ${index + 1}`,
        color: option.color,
        total: proposalOptionDisplayTotal(
          option,
          actionable && membershipSelections[index] === true,
          billingIdFor(option, index),
        ),
        recommended: option.recommended,
      }))}
      activeIndex={activeIndex}
      onSelect={onSelect}
    >
      {documentProps ? (
        singleScope ? (
          <ProposalOptionDocument
            {...documentProps}
            showItemImages={layoutVisibility(proposal.resolvedDocLayout).itemImages}
          />
        ) : (
          <ProposalItemizedOptionDocument {...documentProps} proposal={proposal} />
        )
      ) : null}
    </ProposalOptionTabs>
  );
}
